import { useState, Fragment } from 'react';
import { Typography, Box, IconButton, ButtonGroup } from '@mui/material';
import { Add, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitleWithButton,
  Pagination,
  Loader,
  Button,
  SearchSection
} from '../../../../components';
import { IResponse, INeighborhoodPunchlist, IError } from '../../../../models';
import { getNeighborhoodPunchlists } from '../../../../fetch';
import { TodoDetailModal } from '../punchlist-item/TodoDetailModal';
import { NeighborhoodAccordion } from './NeighborhoodAccordion';
import { PunchlistFilters } from '../PunchlistFilters';

export const NeighborhoodPunchlists = () => {
  const [territoryId, setTerritoryId] = useState<string | number>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [filters, setFilters] = useState<{
    search?: string;
    territoryId?: number | string;
  } | null>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isTodoModalOpen, showTodoModal] = useState(false);
  const [isAccordionsExpanded, setAccordionsExpanded] = useState<boolean>(true);
  // context
  const { enqueueSnackbar } = useSnackbar();
  // queries
  const {
    isLoading: isLoadingPunchlists,
    data: neighborhoodPunchlistData,
    refetch: refetchNeighborhoodPunchlist
  } = useQuery<IResponse<INeighborhoodPunchlist[]>, Error>(
    ['neighborPunchlists', page, perPage, filters, searchedValue],
    () =>
      getNeighborhoodPunchlists({
        page: page + 1,
        perPage,
        search: searchedValue,
        ...filters
      }),

    {
      onSuccess: (d: any) => {
        // make sure the call doesn't refresh again if we hit an error
        if (!hasError && d?.Detail) {
          const error: IError = d;
          enqueueSnackbar(error.Detail, {
            variant: 'error'
          });
          return setHasError(true);
        }
        const data: IResponse<INeighborhoodPunchlist[]> = d;
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        // make sure the call doesn't refresh again if we hit an error
        if (!hasError) {
          enqueueSnackbar(e?.response?.data?.Detail || `Error loading punchlist recent activity.`, {
            variant: 'error'
          });
          setHasError(true);
        }
      },
      enabled: true,
      // whenever props changes, refetch the data
      notifyOnChangeProps: 'tracked',
      staleTime: Infinity
    }
  );
  const neighborhoodPunchlists = neighborhoodPunchlistData?.records ?? [];
  return (
    <Page title="Neighborhood Punchlists">
      <PageTitleWithButton
        pageTitleProps={{
          marginBottom: false,
          title: 'Neighborhood Punchlists'
        }}
        button={
          <ButtonGroup>
            <Button
              id="create-punchlist"
              asLink
              to="/production/punchlists/create-punchlist"
              startIcon={<Add />}
              color="primary"
              variant="contained"
            >
              Create Punchlist
            </Button>
            <Button
              id="add-todo"
              onClick={() => {
                showTodoModal(true);
              }}
              variant="contained"
              color="primary"
              startIcon={<Add />}
            >
              Add Todo
            </Button>
          </ButtonGroup>
        }
      />
      <PunchlistFilters
        isLoading={isLoadingPunchlists}
        selectedTerritoryId={territoryId}
        setSelectedTerritoryId={setTerritoryId}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        setSearchValue={setSearchValue}
        setSearchedValue={setSearchedValue}
        searchValue={searchValue}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              territoryId: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              territoryId: territoryId || undefined
            });
          }
        }}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearchValue('');
            setSearchedValue('');
            setHasAppliedFilters(false);
            setPage(0);
          }}
        />
      )}
      {isLoadingPunchlists && <Loader position="centered" type="inline" />}
      {!isLoadingPunchlists && neighborhoodPunchlists?.length === 0 && (
        <Box display="flex" justifyContent="center" marginTop="1rem">
          <Typography variant="body1" gutterBottom>
            No neighborhood punchlists found.
          </Typography>
        </Box>
      )}
      {!isLoadingPunchlists && neighborhoodPunchlists?.length > 0 && (
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            onClick={() => setAccordionsExpanded(!isAccordionsExpanded)}
            title={`${isAccordionsExpanded ? 'Collapse' : 'Expand'} all punchlists`}
          >
            {isAccordionsExpanded ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
        </Box>
      )}
      {!isLoadingPunchlists &&
        neighborhoodPunchlists?.map((item, index) => {
          return (
            <Fragment key={`${index}`}>
              <NeighborhoodAccordion
                neighborhoodItem={item}
                isAllAccodionsExpanded={isAccordionsExpanded}
              />
            </Fragment>
          );
        })}
      {!isLoadingPunchlists && neighborhoodPunchlists?.length > 0 && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <TodoDetailModal
        open={isTodoModalOpen}
        handleClose={(shouldRefresh?: boolean, shouldClose = true) => {
          if (shouldRefresh) {
            refetchNeighborhoodPunchlist();
          }
          if (shouldClose) showTodoModal(false);
        }}
      />
    </Page>
  );
};
